import { KeyboardEvent, RefObject, useEffect, useMemo, useRef, useState } from "react";
import { EditSectionModal } from "../EditSectionModal/EditSectionModal";
import styles from "./EditCompetitorsModal.module.scss";
import { SearchInput } from "@/frontend/shared/ui/SearchInput";
import { useQuery, useMutation } from "@tanstack/react-query";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useDebounce } from "@/frontend/shared/lib/useDebounce/useDebounce";
import { CompaniesDropdown, TFormattedSearchedCompanies } from "../CompaniesDropdown/CompaniesDropdown";
import { Input } from "@/frontend/shared/ui/Input";
import { Typography } from "@/frontend/shared/ui/Typography";
import { UserIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { EllipsisIcon } from "@/frontend/shared/assets/icons/EllipsisIcon";
import { Droppable, Draggable, DragDropContext, DropResult } from "@hello-pangea/dnd";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { refactorWebsiteLink } from "@/frontend/shared/lib/refactorWebsiteLink/refactorWebsiteLink";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { postBulkAddCompetitors } from "../../model/services/postBulkAddCompetitors";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { postAddCompetitor } from "../../model/services/postAddCompetitor";
import { postSearchCompetitorCompanies } from "../../model/services/postSearchCompetitorCompanies";
import { Competitor, TCompanyData } from "@/frontend/features/Company/model/types/types";
import { Logo } from "@/frontend/shared/ui/Logo";
import { validateText } from "@/frontend/shared/lib/validations/validateText";
import { validateWebsite } from "@/frontend/shared/lib/validations/validateWebsite";
import { validateLinkedInUrl } from "@/frontend/shared/lib/validations/validateLinkedInUrl";

type TEditCompetitorsModal = {
  competitors: Competitor[];
  isOpen: boolean;
  onClose: () => void;
  triggerRefetch: () => void;
};

export enum ESection {
  COMPETITORS_LIST = "competitors_list",
  ADD_COMPETITOR = "add_competitor"
}

export type TCompetitorsData = {
  name: string;
  website: string;
  linkedIn: string;
}[];

export function EditCompetitorsModal({ competitors, isOpen, onClose, triggerRefetch }: TEditCompetitorsModal) {
  const [isSearchOpened, setIsSearchOpened] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [companyValue, setCompanyValue] = useState<string>("");
  const dropdownRef = useRef<null | HTMLDivElement>(null);
  const companiesContainerRef = useRef<null | HTMLDivElement>(null);
  const [section, setSection] = useState<ESection>(ESection.COMPETITORS_LIST);
  const modalContentRef = useRef<null | HTMLDivElement>(null);
  const [addCompetitorData, setAddCompetitorData] = useState({
    id: 0,
    headline: "",
    logoUrl: "",
    name: "",
    website: "",
    linkedInUrl: ""
  });

  const [competitorsData, setCompetitorsData] = useState<TFormattedSearchedCompanies[]>([]);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState({ isOpen: false, id: 0 });
  const [isLoadingAddCompetitor, setIsLoadingAddCompetitor] = useState(false);
  const [inputErrors, setInputErrors] = useState<{ name?: string; website?: string; linkedIn?: string }>({});
  const addCompetitorsMutation = useMutation({
    mutationKey: ["addCompetitors"],
    mutationFn: async (competitorsIds: number[]) => {
      await postBulkAddCompetitors(competitorsIds);
    },
    gcTime: 0
  });

  function formatCompetitors(competitors: Competitor[]) {
    const formatted: TFormattedSearchedCompanies[] = [];
    for (const c of competitors) {
      formatted.push({
        id: c.id,
        name: c.name ?? "",
        headline: c?.headline ?? "",
        logoUrl: c?.logoUrl ?? "",
        website: c?.website ?? "",
        linkedInUrl: c?.linkedInUrl ?? ""
      });
    }
    return formatted;
  }

  useEffect(() => {
    setCompetitorsData(formatCompetitors(competitors));
  }, [competitors]);

  const {
    data: searchedCompanies,
    isFetching: isFetchingSearchedCompanies,
    refetch: refetchSearchedCompanies
  } = useQuery({
    queryKey: ["competitiveSearchedCompanies", companyValue],
    queryFn: async () => {
      try {
        const res = await postSearchCompetitorCompanies({
          name: companyValue,
          page: 1,
          limit: 10
        });
        return res || [];
      } catch (error) {
        console.log(error);
        snackbarConstructor("Error searching companies", "error");
        return [];
      }
    },
    refetchOnWindowFocus: false,
    enabled: isSearchOpened,
    throwOnError: (error) => {
      console.error(error);
      snackbarConstructor("Error searching companies", "error");
      reportErrorToSentry(error, "EditCompetitorsModal: /search/companies");
      return false;
    }
  });

  const debouncedSearch = useDebounce((value) => {
    setCompanyValue(value);
  }, 400);

  function handleInputChange(value: string) {
    setInputValue(value);
    debouncedSearch(value);
  }

  function onKeyDownEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      refetchSearchedCompanies();
      setIsSearchOpened(true);
    }
  }

  const formattedSearchedCompanies = useMemo(() => {
    if (searchedCompanies) {
      return searchedCompanies?.companies
        ?.filter(
          (company: TCompanyData) =>
            company.name && competitorsData && !competitorsData.some((c) => c.id === company.id)
        )
        ?.slice(0, 5)
        ?.map((company: TCompanyData) => ({
          id: company.id,
          name: company.name,
          headline: company.headline,
          logoUrl: company.logoUrl,
          website: company.website,
          linkedInUrl: company.linkedInUrl
        }));
    }

    return [];
  }, [searchedCompanies, competitorsData]);

  function useOutsideClick(refs: RefObject<HTMLElement>[], callback: () => void) {
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (refs.every((ref) => ref.current && !ref.current.contains(event.target as Node))) {
          callback();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [refs, callback]);
  }

  useOutsideClick([dropdownRef, companiesContainerRef], () => {
    if (isSearchOpened) {
      setIsSearchOpened(false);
    }
  });

  useEffect(() => {
    if (modalContentRef.current && isSearchOpened) {
      modalContentRef.current.style.overflow = "hidden";
    } else {
      modalContentRef.current?.style.removeProperty("overflow");
    }
  }, [isSearchOpened]);

  function compareCompetitors(competitors: Competitor[], competitorsData: TFormattedSearchedCompanies[]): boolean {
    for (const company of competitorsData) {
      if (!company.id) {
        return false;
      }
    }

    if (competitors.length !== competitorsData.length) {
      return false;
    }

    for (let i = 0; i < competitors.length; i++) {
      const originalCompany = competitors[i];
      const comparedCompany = competitorsData[i];

      if (
        originalCompany.id !== comparedCompany.id ||
        originalCompany.name !== comparedCompany.name ||
        originalCompany.website !== comparedCompany.website ||
        originalCompany.headline !== comparedCompany.headline
      ) {
        return false;
      }
    }

    return true;
  }

  function onCloseModal() {
    setInputErrors({});
    if (section === ESection.ADD_COMPETITOR) {
      setIsOpenConfirmationModal(true);
    } else if (section === ESection.COMPETITORS_LIST && !compareCompetitors(competitors, competitorsData)) {
      setIsOpenConfirmationModal(true);
    } else {
      onClose();
    }
  }

  function onDiscard() {
    if (section === ESection.ADD_COMPETITOR) {
      setSection(ESection.COMPETITORS_LIST);
      setAddCompetitorData({} as TFormattedSearchedCompanies);
      setIsOpenConfirmationModal(false);
    } else {
      setIsOpenConfirmationModal(false);
      setAddCompetitorData({} as TFormattedSearchedCompanies);
      setCompetitorsData(formatCompetitors(competitors));
      onClose();
    }
  }

  async function onDeleteCompetitor(id: number) {
    setCompetitorsData((prev) => prev.filter((item) => item.id !== id));
    setIsOpenDeleteConfirmation({ isOpen: false, id: 0 });
  }

  function onDragEnd(result: DropResult) {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) {
      return;
    }
    setCompetitorsData((currentItems) => {
      const newItems = Array.from(currentItems);
      const [reorderedItem] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, reorderedItem);

      return newItems;
    });
  }

  function onChangeCompetitorWebsite(value: string) {
    setAddCompetitorData((prev) => ({ ...prev, website: value }));
  }

  function onChangeCompetitorName(value: string) {
    setAddCompetitorData((prev) => ({ ...prev, name: value }));
  }

  function onChangeCompetitorLinkedInUrl(value: string) {
    setAddCompetitorData((prev) => ({ ...prev, linkedInUrl: value }));
  }

  function content() {
    switch (true) {
      case section === ESection.COMPETITORS_LIST:
        return (
          <div className={styles.searchContainer} ref={dropdownRef}>
            <SearchInput
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={onKeyDownEnter}
              onClick={(e) => {
                e.stopPropagation();
                setIsSearchOpened(true);
              }}
            />
            <CompaniesDropdown
              className={styles.companiesContainer}
              inputValue={inputValue}
              options={formattedSearchedCompanies}
              isOpen={isSearchOpened}
              setInputValue={setInputValue}
              onClose={() => setIsSearchOpened(false)}
              isCompaniesLoading={isFetchingSearchedCompanies}
              setSection={setSection}
              companiesContainerRef={companiesContainerRef}
              setCompetitorsData={setCompetitorsData}
            />
            <Droppable droppableId="comapies">
              {(provided) => {
                return (
                  competitorsData.length > 0 && (
                    <div className={styles.companiesBlock} ref={provided.innerRef} {...provided.droppableProps}>
                      {competitorsData.map((company, index) => (
                        <Draggable key={index} draggableId={`item-${index}`} index={index}>
                          {(provided) => (
                            <div
                              key={index}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={styles.company}
                            >
                              <a
                                className={styles.companyContent}
                                href={refactorWebsiteLink(company.website)}
                                rel="noreferrer"
                                target="_blank"
                                {...provided.dragHandleProps}
                              >
                                <div className={styles.dragIcon}>
                                  <EllipsisIcon className={styles.ellipsisIcon} />
                                </div>
                                {/* {company.logoUrl ? (
                                  <img src={company.logoUrl} alt="company" className={styles.image} />
                                ) : (
                                  <div className={styles.image}>
                                    <UserIcon className={styles.userIcon} />
                                  </div>
                                )} */}
                                <Logo logo={company.logoUrl} />
                                <div className={styles.companyInfo}>
                                  <Typography variant="label-4" color="greyscale900">
                                    {company.name}
                                  </Typography>
                                  <Typography variant="body-3" color="greyscale600">
                                    {company.headline}
                                  </Typography>
                                </div>
                              </a>
                              <div className={styles.iconsContainer}>
                                <TrashIcon
                                  onClick={() => setIsOpenDeleteConfirmation({ isOpen, id: company.id })}
                                  className={styles.trashIcon}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )
                );
              }}
            </Droppable>
          </div>
        );

      case section === ESection.ADD_COMPETITOR:
        return (
          <div className={styles.addCompanyContainer}>
            <div className={styles.modalSection}>
              <Typography variant="label-4" color="greyscale700" className={styles.label}>
                Name
              </Typography>
              <Input
                className={styles.input}
                value={addCompetitorData.name}
                onChange={(e) => {
                  onChangeCompetitorName(e.target.value);
                }}
                error={inputErrors.name}
              />
            </div>
            <div className={styles.modalSection}>
              <Typography variant="label-4" color="greyscale700" className={styles.label}>
                Website
              </Typography>
              <Input
                className={styles.input}
                value={addCompetitorData.website}
                onChange={(e) => onChangeCompetitorWebsite(e.target.value)}
                error={inputErrors.website}
              />
            </div>
            <div className={styles.modalSection}>
              <Typography variant="label-4" color="greyscale700" className={styles.label}>
                LinkedIn
              </Typography>
              <Input
                className={styles.input}
                value={addCompetitorData.linkedInUrl}
                onChange={(e) => onChangeCompetitorLinkedInUrl(e.target.value)}
                error={inputErrors.linkedIn}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  function validate() {
    setInputErrors({});

    const nameError = validateText({ text: addCompetitorData.name, fieldName: "Name", maxLength: 70 });
    const websiteError = validateWebsite({ website: addCompetitorData.website });
    const linkedInError = validateLinkedInUrl({ linkedInUrl: addCompetitorData.linkedInUrl, required: false });

    setInputErrors({
      name: nameError,
      website: websiteError,
      linkedIn: linkedInError
    });

    return !nameError && !websiteError && !linkedInError;
  }

  async function onSave() {
    if (section === ESection.ADD_COMPETITOR) {
      if (!validate()) {
        snackbarConstructor("Please correct the errors before saving.", "error");
        return;
      }

      setIsLoadingAddCompetitor(true);

      const res = await postAddCompetitor({
        name: addCompetitorData.name,
        website: addCompetitorData.website,
        linkedIn: addCompetitorData.linkedInUrl
      });

      setIsLoadingAddCompetitor(false);

      if (!res) {
        snackbarConstructor("Failed to add competitor", "error");
        return;
      }

      setCompetitorsData((prev) => [...prev, { ...res, linkedInUrl: res.linkedinUrl }]);

      setSection(ESection.COMPETITORS_LIST);
      setAddCompetitorData({} as TFormattedSearchedCompanies);
    } else if (section === ESection.COMPETITORS_LIST) {
      const competitorsIds = competitorsData.map((item) => item.id);
      await addCompetitorsMutation.mutateAsync(competitorsIds);
      triggerRefetch();
      snackbarConstructor("Competitors changes have been saved!", "normal");
      onClose();
    }
  }

  return (
    <EditSectionModal
      isOpen={isOpen}
      onClose={onCloseModal}
      title={section === ESection.COMPETITORS_LIST ? "Edit Competitors" : "Add Company"}
      onSave={onSave}
      saveButtonName={section === ESection.COMPETITORS_LIST ? "Save" : "Add Company"}
      modalContentRef={modalContentRef}
      isLoading={addCompetitorsMutation.isPending || isLoadingAddCompetitor}
      cancelButton={
        section === ESection.ADD_COMPETITOR ? (
          <NormalButton
            className={styles.cancelNormalButton}
            size="medium"
            onClick={() => setIsOpenConfirmationModal(true)}
            variant="secondary"
          >
            Cancel
          </NormalButton>
        ) : null
      }
    >
      <DragDropContext onDragEnd={onDragEnd}>{content()}</DragDropContext>
      <ConfirmationModal
        isOpen={isOpenConfirmationModal}
        onClose={() => setIsOpenConfirmationModal(false)}
        onConfirm={onDiscard}
        content={"Are you sure you want to discard the changes you made?"}
        title={"Discard Changes"}
        confirmButtonText={"Discard"}
        cancelButtonText={"No Thanks"}
      />
      <ConfirmationModal
        isOpen={isOpenDeleteConfirmation.isOpen}
        onClose={() => setIsOpenDeleteConfirmation({ isOpen: false, id: 0 })}
        onConfirm={() => onDeleteCompetitor(isOpenDeleteConfirmation.id)}
        content={"Are you sure you want to delete competitor?"}
        title={"Delete Competitor"}
        dangerConfirm
        confirmButtonText={"Delete"}
        cancelButtonText={"Cancel"}
      />
    </EditSectionModal>
  );
}
